/**
 * Elite Batch 5 Part 5 — Workflow Templates 428–447
 * Marketplace KYC, Meta Catalog, Mobile Recovery, Inventory, Cart Recovery,
 * Product Listing, Google Shopping, Arrivals, Onboarding, Order Confirmation & Delivery
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function shopifyNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://{shopify_store}.myshopify.com/admin/api/2024-01/${endpoint}`,
        headers: [
          { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendSMS",
    label,
    y,
    { sendSMSDetails: { to, content: body } },
    x,
  );
}
function pushNode(
  id: string,
  label: string,
  y: number,
  title: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "pushNotification",
    label,
    y,
    { pushNotificationDetails: { title, body } },
    x,
  );
}
function hubspotNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function zendeskNode(
  id: string,
  label: string,
  y: number,
  subject: string,
  body: string,
  priority: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets.json",
        headers: [
          { key: "Authorization", value: "Bearer {zendesk_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          {
            key: "ticket",
            value: `{"subject":"${subject}","comment":{"body":"${body}"},"priority":"${priority}"}`,
          },
        ],
        variables: vars,
      },
    },
    x,
  );
}
function stripeNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://api.stripe.com/v1/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {stripe_secret_key}" },
          { key: "Content-Type", value: "application/x-www-form-urlencoded" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 5 PART 5 — TEMPLATES 428–447
// =============================================================================

export const ELITE_BATCH5_PART5: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 428. MARKETPLACE SELLER KYC BOT (9 nodes)
  // ===================================================================

  "Marketplace Seller KYC Bot": {
    summary:
      "Automates seller onboarding KYC from intake to compliance. A seller registration triggers structured AI KYC intake, collects documents, validates completeness via runCode, verifies identity through a third-party API, routes on KYC status, escalates failures to compliance Slack, emails the seller, and logs the audit trail to Sheets.",
    nodes: [
      trigger("Webhook: Seller onboarding registration", "webhook_app", {
        source: "Marketplace Portal",
        event: "seller.registration.created",
        howItStarts:
          "A new seller registers on the marketplace and submits onboarding data (business name, tax ID, identity docs, bank info)",
        setup:
          "Connect your marketplace registration form or portal to POST seller onboarding data to the workflow URL.",
        inputFields: [],
      }),

      // 2. Structured KYC intake (AI)
      aiNode(
        "2",
        "AI structured KYC intake",
        280,
        "You are a KYC intake specialist. Parse the seller registration payload and extract structured KYC fields regardless of format. Identify the business name, legal entity type, tax ID, country, registered address, beneficial owner name, owner DOB, ID document type, ID document number, and bank account details. Flag any missing required fields. Respond JSON: {business_name: string, entity_type: string, tax_id: string, country: string, address: string, owner_name: string, owner_dob: string, id_doc_type: string, id_doc_number: string, bank_account_last4: string, missing_fields: string, seller_email: string, risk_tier: string}",
        "{webhook_payload}",
        [
          { field: "business_name", as: "business_name", type: "string" },
          { field: "entity_type", as: "entity_type", type: "string" },
          { field: "tax_id", as: "tax_id", type: "string" },
          { field: "country", as: "country", type: "string" },
          { field: "owner_name", as: "owner_name", type: "string" },
          { field: "id_doc_type", as: "id_doc_type", type: "string" },
          { field: "id_doc_number", as: "id_doc_number", type: "string" },
          { field: "missing_fields", as: "missing_fields", type: "string" },
          { field: "seller_email", as: "seller_email", type: "string" },
          { field: "risk_tier", as: "risk_tier", type: "string" },
        ],
      ),

      // 3. Document collection (fetch uploaded docs)
      n("3", "hitApi", "Fetch uploaded KYC documents", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{marketplace_api}/api/v1/sellers/{seller_id}/documents",
          headers: [
            { key: "Authorization", value: "Bearer {marketplace_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "documents.length",
              customName: "doc_count",
              type: "number",
            },
            {
              name: "documents[0].type",
              customName: "primary_doc_type",
              type: "string",
            },
            {
              name: "documents[0].status",
              customName: "primary_doc_status",
              type: "string",
            },
          ],
        },
      }),

      // 4. Completeness validation (runCode)
      n("4", "runCode", "Validate KYC completeness", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate KYC document completeness
const requiredDocs = ["government_id", "proof_of_address", "tax_certificate"];
const entityDocs = { "corporation": ["articles_of_incorporation", "board_resolution"], "llc": ["operating_agreement"], "partnership": ["partnership_agreement"] };
const entityType = vars.entity_type || "sole_proprietor";
const allRequired = [...requiredDocs, ...(entityDocs[entityType] || [])];
const submitted = parseInt(vars.doc_count || "0", 10);
const missingFields = vars.missing_fields || "";
const hasMissingData = missingFields.length > 0 && missingFields !== "none";
const docsComplete = submitted >= allRequired.length;
const result = {
  kyc_complete: docsComplete && !hasMissingData ? "yes" : "no",
  docs_required: allRequired.length,
  docs_submitted: submitted,
  docs_gap: Math.max(0, allRequired.length - submitted),
  data_complete: hasMissingData ? "no" : "yes",
  validation_summary: docsComplete && !hasMissingData ? "All documents and data received" : "Missing " + (allRequired.length - submitted) + " documents and/or data fields: " + missingFields
};
return result;`,
          outputVariables: [
            { name: "kyc_complete", type: "string" },
            { name: "docs_required", type: "number" },
            { name: "docs_submitted", type: "number" },
            { name: "docs_gap", type: "number" },
            { name: "data_complete", type: "string" },
            { name: "validation_summary", type: "string" },
          ],
        },
      }),

      // 5. Identity verification (third-party)
      n("5", "hitApi", "Identity verification (Onfido/Jumio)", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.onfido.com/v3.6/checks",
          headers: [
            { key: "Authorization", value: "Token token={onfido_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "applicant_id", value: "{seller_id}" },
            {
              key: "report_names",
              value: '["identity_enhanced","watchlist_standard"]',
            },
          ],
          variables: [
            { name: "id", customName: "check_id", type: "string" },
            {
              name: "status",
              customName: "verification_status",
              type: "string",
            },
            {
              name: "result",
              customName: "verification_result",
              type: "string",
            },
          ],
        },
      }),

      // 6. KYC status condition
      n("6", "condition", "KYC verification passed?", 1240, {
        conditionData: [
          {
            variable: "verification_result",
            value: "clear",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
          {
            variable: "kyc_complete",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Compliance escalation (Slack) — False path
      slackNode(
        "7",
        "Slack: Compliance escalation",
        1480,
        ":rotating_light: KYC Escalation Required\\nSeller: {business_name} ({entity_type})\\nOwner: {owner_name}\\nCountry: {country}\\nVerification: {verification_result}\\nCompleteness: {validation_summary}\\nDocs gap: {docs_gap}\\nRisk tier: {risk_tier}\\nCheck ID: {check_id}",
        400,
      ),

      // 8. Seller notification email
      emailNode(
        "8",
        "Email seller KYC status",
        1480,
        "{seller_email}",
        "Your Seller Account Verification Update - {business_name}",
        "Hello {owner_name},\\n\\nThank you for registering {business_name} on our marketplace.\\n\\nVerification Status: {verification_result}\\nDocument Review: {validation_summary}\\n\\nIf your verification is complete, your seller account will be activated within 24 hours. If additional documents are needed, please upload them through your seller dashboard.\\n\\nThank you for your patience.",
      ),

      // 9. KYC audit logging (Sheets)
      sheetsNode(
        "9",
        "Log KYC audit to Sheets",
        1720,
        "KYCAudit",
        '[["{business_name}","{entity_type}","{country}","{owner_name}","{tax_id}","{verification_result}","{kyc_complete}","{docs_submitted}","{docs_required}","{risk_tier}","{check_id}","{validation_summary}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "8", "output_1"), // passed -> seller notification
      e("6", "7", "output_2"), // failed -> compliance escalation
      e("7", "8"), // after escalation, still notify seller
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 429. META CATALOG PRICE/STOCK UPDATE (7 nodes)
  // ===================================================================

  "Meta Catalog Price/Stock Update": {
    summary:
      "Syncs Shopify price and stock changes to Meta catalog in efficient batches. A Shopify product update webhook triggers batch-window aggregation via runCode, transforms data to Meta catalog format, pushes via Meta Batch API, validates catalog health, alerts marketing on rejections via Slack, and logs every update to Sheets.",
    nodes: [
      trigger("Webhook: Shopify product price/stock change", "webhook_app", {
        source: "Shopify",
        event: "products/update",
        howItStarts:
          "A product price or inventory level changes in Shopify, triggering a sync to the Meta product catalog",
        setup:
          "Configure Shopify webhook for products/update events. Set up Meta Business API credentials and catalog ID.",
        inputFields: [],
      }),

      // 2. Batch window aggregation (runCode)
      n("2", "runCode", "Batch window aggregation", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Aggregate product changes into a batch for Meta catalog update
const payload = vars.webhook_payload ? JSON.parse(vars.webhook_payload) : {};
const variants = payload.variants || [];
const items = variants.map(v => ({
  retailer_id: String(v.id || v.sku),
  availability: (v.inventory_quantity || 0) > 0 ? "in stock" : "out of stock",
  price: v.price ? v.price + " USD" : "0.00 USD",
  sale_price: v.compare_at_price && parseFloat(v.compare_at_price) > parseFloat(v.price) ? v.price + " USD" : "",
  inventory_count: v.inventory_quantity || 0
}));
const result = {
  batch_items: JSON.stringify(items),
  item_count: items.length,
  product_id: String(payload.id || ""),
  product_title: payload.title || "",
  has_sale_prices: items.some(i => i.sale_price !== "") ? "yes" : "no",
  total_inventory: items.reduce((sum, i) => sum + i.inventory_count, 0)
};
return result;`,
          outputVariables: [
            { name: "batch_items", type: "string" },
            { name: "item_count", type: "number" },
            { name: "product_id", type: "string" },
            { name: "product_title", type: "string" },
            { name: "has_sale_prices", type: "string" },
            { name: "total_inventory", type: "number" },
          ],
        },
      }),

      // 3. Meta catalog format transform
      aiNode(
        "3",
        "Transform to Meta catalog format",
        520,
        "You are a Meta Commerce API specialist. Given batch items from Shopify, transform them into Meta product catalog batch update format. Each item needs retailer_id, availability, price (with currency), and sale_price if applicable. Validate all prices have correct currency format. Respond JSON: {batch_payload: string, valid_items: number, invalid_items: number, validation_notes: string}",
        "Batch items: {batch_items} | Product: {product_title} (ID: {product_id}) | Has sales: {has_sale_prices}",
        [
          { field: "batch_payload", as: "batch_payload", type: "string" },
          { field: "valid_items", as: "valid_items", type: "number" },
          { field: "invalid_items", as: "invalid_items", type: "number" },
          { field: "validation_notes", as: "validation_notes", type: "string" },
        ],
      ),

      // 4. Batch push (Meta API)
      n("4", "hitApi", "Push batch to Meta catalog", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v19.0/{meta_catalog_id}/items_batch",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "access_token", value: "{meta_access_token}" },
            { key: "requests", value: "{batch_payload}" },
          ],
          variables: [
            { name: "handles", customName: "batch_handle", type: "string" },
            {
              name: "validation_status",
              customName: "meta_validation_status",
              type: "string",
            },
          ],
        },
      }),

      // 5. Health validation
      n("5", "hitApi", "Check Meta catalog health", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://graph.facebook.com/v19.0/{meta_catalog_id}/check_batch_request_status?handle={batch_handle}",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
          ],
          body: [],
          variables: [
            {
              name: "data[0].status",
              customName: "batch_status",
              type: "string",
            },
            {
              name: "data[0].errors_total_count",
              customName: "error_count",
              type: "number",
            },
            {
              name: "data[0].num_received",
              customName: "items_received",
              type: "number",
            },
          ],
        },
      }),

      // 6. Marketing Slack alert on rejections
      slackNode(
        "6",
        "Slack: Meta catalog sync alert",
        1240,
        ":package: Meta Catalog Update\\nProduct: {product_title} (ID: {product_id})\\nItems synced: {item_count} | Valid: {valid_items} | Errors: {error_count}\\nBatch status: {batch_status}\\nInventory total: {total_inventory}\\nSale prices: {has_sale_prices}\\nValidation: {validation_notes}",
      ),

      // 7. Update logging (Sheets)
      sheetsNode(
        "7",
        "Log catalog update to Sheets",
        1480,
        "MetaCatalogSync",
        '[["{product_id}","{product_title}","{item_count}","{valid_items}","{error_count}","{batch_status}","{total_inventory}","{has_sale_prices}","{validation_notes}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 430. META CATALOG SEASONAL REFRESH (8 nodes)
  // ===================================================================

  "Meta Catalog Seasonal Refresh": {
    summary:
      "Refreshes Meta product catalog with seasonal copy and imagery for campaign periods. A scheduled seasonal campaign trigger pulls tagged products from Shopify, generates AI seasonal copy, maps seasonal imagery, updates the Meta catalog, schedules automatic reversion after the season, verifies the catalog, and logs performance to Sheets.",
    nodes: [
      trigger("Schedule: Seasonal campaign launch", "schedule", {
        schedule:
          "Triggered at season start (e.g., cron for Black Friday, Summer Sale, Holiday)",
        howItStarts:
          "A scheduled seasonal campaign triggers a full refresh of Meta catalog content with seasonal copy, imagery, and pricing",
        setup:
          "Configure seasonal campaign schedule. Tag seasonal products in Shopify with season-specific tags. Prepare seasonal image URLs. Set Meta catalog and Shopify credentials.",
        inputFields: [],
      }),

      // 2. Tagged product pull (Shopify)
      shopifyNode(
        "2",
        "Pull seasonal tagged products",
        280,
        "GET",
        "products.json?tag=seasonal-active&limit=250",
        [],
        [
          {
            name: "products.length",
            customName: "product_count",
            type: "number",
          },
          { name: "products", customName: "seasonal_products", type: "string" },
        ],
      ),

      // 3. AI seasonal copy
      aiNode(
        "3",
        "AI generate seasonal copy",
        520,
        "You are a seasonal marketing copywriter for e-commerce. Given a list of seasonal products, generate compelling seasonal titles, descriptions, and promotional overlays for each product. Incorporate urgency, seasonal themes, and promotional pricing language. Respond JSON: {seasonal_items: string, copy_count: number, theme_applied: string, urgency_phrases: string, avg_copy_length: number}",
        "Products: {seasonal_products} | Season: current seasonal campaign | Product count: {product_count}",
        [
          { field: "seasonal_items", as: "seasonal_items", type: "string" },
          { field: "copy_count", as: "copy_count", type: "number" },
          { field: "theme_applied", as: "theme_applied", type: "string" },
          { field: "urgency_phrases", as: "urgency_phrases", type: "string" },
        ],
      ),

      // 4. Imagery mapping
      n("4", "runCode", "Map seasonal imagery overlays", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Map seasonal imagery to products
const items = JSON.parse(vars.seasonal_items || "[]");
const imageBase = vars.seasonal_image_base || "https://cdn.example.com/seasonal/";
const season = vars.theme_applied || "default";
const mapped = items.map(item => ({
  ...item,
  seasonal_image_url: imageBase + season + "/" + (item.retailer_id || item.id) + ".jpg",
  overlay_badge: season.includes("holiday") ? "Holiday Special" : season.includes("summer") ? "Summer Sale" : "Limited Time"
}));
const result = {
  mapped_items: JSON.stringify(mapped),
  mapped_count: mapped.length,
  imagery_base: imageBase + season + "/",
  badge_type: mapped[0] ? mapped[0].overlay_badge : "Limited Time"
};
return result;`,
          outputVariables: [
            { name: "mapped_items", type: "string" },
            { name: "mapped_count", type: "number" },
            { name: "imagery_base", type: "string" },
            { name: "badge_type", type: "string" },
          ],
        },
      }),

      // 5. Meta catalog content update
      n("5", "hitApi", "Update Meta catalog seasonal content", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v19.0/{meta_catalog_id}/items_batch",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "access_token", value: "{meta_access_token}" },
            { key: "item_type", value: "PRODUCT_ITEM" },
            { key: "requests", value: "{mapped_items}" },
          ],
          variables: [
            {
              name: "handles",
              customName: "seasonal_batch_handle",
              type: "string",
            },
            {
              name: "validation_status",
              customName: "seasonal_push_status",
              type: "string",
            },
          ],
        },
      }),

      // 6. Reversion scheduling (delay)
      n("6", "delayAction", "Delay until season end for reversion", 1240, {
        delayDetails: { delayType: "fixed", days: 14, hours: 0, minutes: 0 },
      }),

      // 7. Seasonal catalog verification
      n("7", "hitApi", "Verify seasonal catalog update", 1480, {
        hitApiDetails: {
          method: "GET",
          url: "https://graph.facebook.com/v19.0/{meta_catalog_id}/check_batch_request_status?handle={seasonal_batch_handle}",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
          ],
          body: [],
          variables: [
            {
              name: "data[0].status",
              customName: "verification_status",
              type: "string",
            },
            {
              name: "data[0].num_received",
              customName: "items_verified",
              type: "number",
            },
            {
              name: "data[0].errors_total_count",
              customName: "verification_errors",
              type: "number",
            },
          ],
        },
      }),

      // 8. Performance logging (Sheets)
      sheetsNode(
        "8",
        "Log seasonal refresh to Sheets",
        1720,
        "SeasonalCatalog",
        '[["{theme_applied}","{product_count}","{copy_count}","{mapped_count}","{seasonal_push_status}","{verification_status}","{items_verified}","{verification_errors}","{badge_type}","{urgency_phrases}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("5", "7"), // verification runs in parallel, doesn't wait for delay
      e("6", "7"), // after delay, also triggers re-verification
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 431. META PRODUCT CATALOG SYNC (7 nodes)
  // ===================================================================

  "Meta Product Catalog Sync": {
    summary:
      "Keeps Meta product catalog in real-time sync with Shopify. A product create/update webhook triggers format normalization via runCode, validates data quality, pushes to Meta catalog API, monitors catalog health, alerts marketing on Slack for issues, and logs every sync event to Sheets.",
    nodes: [
      trigger("Webhook: Shopify product create/update", "webhook_app", {
        source: "Shopify",
        event: "products/create | products/update",
        howItStarts:
          "A product is created or updated in Shopify, triggering a sync to the Meta product catalog",
        setup:
          "Configure Shopify webhooks for products/create and products/update. Set up Meta Business API access token and catalog ID.",
        inputFields: [],
      }),

      // 2. Meta format normalization (runCode)
      n("2", "runCode", "Normalize to Meta catalog format", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Transform Shopify product data to Meta catalog format
const p = vars.webhook_payload ? JSON.parse(vars.webhook_payload) : {};
const variant = p.variants && p.variants[0] ? p.variants[0] : {};
const images = p.images || [];
const metaItem = {
  retailer_id: String(variant.sku || variant.id || p.id),
  name: p.title || "",
  description: (p.body_html || "").replace(/<[^>]*>/g, "").substring(0, 5000),
  availability: (variant.inventory_quantity || 0) > 0 ? "in stock" : "out of stock",
  price: (variant.price || "0.00") + " USD",
  url: "https://" + (vars.shopify_store || "store") + ".myshopify.com/products/" + (p.handle || ""),
  image_url: images[0] ? images[0].src : "",
  brand: p.vendor || "",
  condition: "new",
  google_product_category: p.product_type || ""
};
const valid = metaItem.name && metaItem.price && metaItem.image_url;
const result = {
  meta_item: JSON.stringify(metaItem),
  product_id: String(p.id || ""),
  product_title: p.title || "",
  retailer_id: metaItem.retailer_id,
  is_valid: valid ? "yes" : "no",
  validation_issues: !metaItem.name ? "missing title" : !metaItem.image_url ? "missing image" : "none"
};
return result;`,
          outputVariables: [
            { name: "meta_item", type: "string" },
            { name: "product_id", type: "string" },
            { name: "product_title", type: "string" },
            { name: "retailer_id", type: "string" },
            { name: "is_valid", type: "string" },
            { name: "validation_issues", type: "string" },
          ],
        },
      }),

      // 3. Validation condition
      n("3", "condition", "Product data valid for Meta?", 520, {
        conditionData: [
          {
            variable: "is_valid",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Meta catalog push
      n("4", "hitApi", "Push product to Meta catalog", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v19.0/{meta_catalog_id}/items_batch",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "access_token", value: "{meta_access_token}" },
            { key: "item_type", value: "PRODUCT_ITEM" },
            { key: "requests", value: "[{meta_item}]" },
          ],
          variables: [
            { name: "handles", customName: "sync_handle", type: "string" },
            {
              name: "validation_status",
              customName: "push_status",
              type: "string",
            },
          ],
        },
      }),

      // 5. Health monitoring
      n("5", "hitApi", "Monitor catalog health", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://graph.facebook.com/v19.0/{meta_catalog_id}?fields=product_count,da_display_settings",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
          ],
          body: [],
          variables: [
            {
              name: "product_count",
              customName: "catalog_total_products",
              type: "number",
            },
          ],
        },
      }),

      // 6. Marketing Slack alert
      slackNode(
        "6",
        "Slack: Meta catalog sync alert",
        1240,
        ":package: Meta Catalog Sync\\nProduct: {product_title} (ID: {product_id})\\nRetailer ID: {retailer_id}\\nPush status: {push_status}\\nValidation: {validation_issues}\\nCatalog total: {catalog_total_products} products",
      ),

      // 7. Sync logging (Sheets)
      sheetsNode(
        "7",
        "Log sync to Sheets",
        1480,
        "MetaCatalogSync",
        '[["{product_id}","{product_title}","{retailer_id}","{is_valid}","{push_status}","{validation_issues}","{catalog_total_products}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // valid -> push to Meta
      e("3", "6", "output_2"), // invalid -> alert marketing
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 432. MOBILE APP BROWSE RECOVERY PUSH (7 nodes)
  // ===================================================================

  "Mobile App Browse Recovery Push": {
    summary:
      "Recovers mobile app browse abandoners with rich push notifications. An app browse abandon event triggers push eligibility check, enriches the product from Shopify, generates AI push copy, sends a rich push notification, tracks delivery and taps, and logs conversions to Sheets.",
    nodes: [
      trigger("Webhook: App browse abandon event", "webhook_app", {
        source: "Mobile App / Firebase / Segment",
        event: "app.browse.abandon",
        howItStarts:
          "A user browses products in the mobile app and leaves without adding to cart or purchasing, triggering a browse recovery push notification",
        setup:
          "Configure your mobile app analytics (Firebase, Segment, or custom) to POST browse abandon events (user ID, product viewed, session duration, device token) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Push eligibility check condition
      n("2", "condition", "Push notifications enabled?", 280, {
        conditionData: [
          {
            variable: "push_enabled",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
          {
            variable: "device_token",
            value: "",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 3. Product enrichment (Shopify)
      shopifyNode(
        "3",
        "Enrich product details from Shopify",
        520,
        "GET",
        "products/{product_id}.json",
        [],
        [
          {
            name: "product.title",
            customName: "product_title",
            type: "string",
          },
          {
            name: "product.variants[0].price",
            customName: "product_price",
            type: "string",
          },
          {
            name: "product.images[0].src",
            customName: "product_image",
            type: "string",
          },
          {
            name: "product.vendor",
            customName: "product_brand",
            type: "string",
          },
          {
            name: "product.variants[0].inventory_quantity",
            customName: "stock_level",
            type: "number",
          },
        ],
      ),

      // 4. AI push copy
      aiNode(
        "4",
        "AI generate push notification copy",
        760,
        "You are a mobile push notification copywriter. Given a product the user browsed, create a compelling push notification with a short title (max 40 chars) and body (max 100 chars). Use urgency if stock is low. Personalize with the user name if available. Include a clear CTA. Respond JSON: {push_title: string, push_body: string, deep_link: string, urgency_level: string, personalization_used: string}",
        "Product: {product_title} | Price: ${product_price} | Brand: {product_brand} | Stock: {stock_level} | User: {user_name} | Browse duration: {session_duration}s",
        [
          { field: "push_title", as: "push_title", type: "string" },
          { field: "push_body", as: "push_body", type: "string" },
          { field: "deep_link", as: "deep_link", type: "string" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
        ],
      ),

      // 5. Rich push notification
      pushNode(
        "5",
        "Send rich push notification",
        1000,
        "{push_title}",
        "{push_body}",
      ),

      // 6. Delivery/tap tracking
      n("6", "hitApi", "Track push delivery and taps", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{analytics_api}/api/v1/events/push-tracking",
          headers: [
            { key: "Authorization", value: "Bearer {analytics_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "event_type", value: "browse_recovery_push" },
            { key: "user_id", value: "{user_id}" },
            { key: "product_id", value: "{product_id}" },
            { key: "push_title", value: "{push_title}" },
          ],
          variables: [
            {
              name: "delivered",
              customName: "push_delivered",
              type: "boolean",
            },
            { name: "tapped", customName: "push_tapped", type: "boolean" },
            { name: "conversion", customName: "converted", type: "boolean" },
          ],
        },
      }),

      // 7. Conversion logging (Sheets)
      sheetsNode(
        "7",
        "Log conversion to Sheets",
        1480,
        "BrowseRecoveryPush",
        '[["{user_id}","{product_title}","{product_price}","{push_title}","{urgency_level}","{push_delivered}","{push_tapped}","{converted}","{stock_level}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3", "output_1"), // push enabled -> enrich product
      e("2", "7", "output_2"), // push disabled -> log only
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 433. MOBILE APP CART PUSH PLUS EMAIL FALLBACK (8 nodes)
  // ===================================================================

  "Mobile App Cart Push Plus Email Fallback": {
    summary:
      "Multi-channel cart recovery starting with push then falling back to email. An app cart abandon triggers push eligibility routing, sends push to eligible users, waits for open/purchase, falls back to email for push-ignored or push-disabled users, suppresses purchasers, and logs channel comparison to Sheets.",
    nodes: [
      trigger("Webhook: App cart abandon event", "webhook_app", {
        source: "Mobile App / Firebase / Segment",
        event: "app.cart.abandon",
        howItStarts:
          "A user adds items to cart in the mobile app and abandons without completing purchase",
        setup:
          "Configure your mobile app to POST cart abandon events (user ID, cart items, cart value, device token, push preferences, email) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Push eligibility condition
      n("2", "condition", "Push enabled for user?", 280, {
        conditionData: [
          {
            variable: "push_enabled",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 3. Push recovery send (True path)
      pushNode(
        "3",
        "Send cart recovery push",
        520,
        "You left something behind!",
        "Your cart ({cart_value}) is waiting. Complete your purchase before items sell out.",
      ),

      // 4. Open/purchase delay check
      n("4", "delayAction", "Wait 2 hours for push engagement", 760, {
        delayDetails: { delayType: "fixed", days: 0, hours: 2, minutes: 0 },
      }),

      // 5. Email fallback for push-ignored (condition after delay)
      n("5", "condition", "Purchased after push?", 1000, {
        conditionData: [
          {
            variable: "purchased",
            value: "true",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Email-only path for push-disabled / email fallback
      emailNode(
        "6",
        "Email cart recovery fallback",
        1240,
        "{customer_email}",
        "Your cart is waiting - {cart_item_count} items inside",
        "Hi {customer_name},\\n\\nYou left {cart_item_count} item(s) worth {cart_value} in your cart.\\n\\nTop item: {cart_primary_item}\\n\\nComplete your purchase now and don't miss out.\\n\\n[Complete Purchase]({cart_recovery_url})",
      ),

      // 7. Purchase suppression
      n("7", "runCode", "Purchase suppression check", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check final purchase status and determine channel attribution
const pushSent = vars.push_enabled === "true";
const purchased = vars.purchased === "true";
const channel = pushSent ? "push_then_email" : "email_only";
const result = {
  final_status: purchased ? "converted" : "not_converted",
  recovery_channel: channel,
  push_attempted: pushSent ? "yes" : "no",
  email_sent: "yes",
  cart_value_numeric: parseFloat(vars.cart_value || "0").toFixed(2),
  suppressed: purchased ? "yes" : "no"
};
return result;`,
          outputVariables: [
            { name: "final_status", type: "string" },
            { name: "recovery_channel", type: "string" },
            { name: "push_attempted", type: "string" },
            { name: "email_sent", type: "string" },
            { name: "cart_value_numeric", type: "string" },
            { name: "suppressed", type: "string" },
          ],
        },
      }),

      // 8. Channel comparison logging (Sheets)
      sheetsNode(
        "8",
        "Log channel comparison to Sheets",
        1720,
        "CartRecoveryChannels",
        '[["{user_id}","{cart_value_numeric}","{cart_item_count}","{recovery_channel}","{push_attempted}","{email_sent}","{final_status}","{suppressed}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3", "output_1"), // push enabled -> send push
      e("2", "6", "output_2"), // push disabled -> email only path
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // not purchased -> email fallback
      e("5", "7", "output_2"), // purchased -> suppression (skip email)
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 434. MULTI CHANNEL INVENTORY SYNC (9 nodes)
  // ===================================================================

  "Multi Channel Inventory Sync": {
    summary:
      "Synchronizes inventory across Shopify and secondary channels with conflict resolution. An inventory change triggers dual-channel pull, detects conflicts via runCode, auto-resolves or escalates, writes back inventory, applies safety stock protection, alerts ops on Slack, logs to Sheets, and tracks oversell incidents.",
    nodes: [
      trigger("Webhook: Inventory change / schedule sync", "webhook_app", {
        source: "Shopify / Warehouse Management System",
        event: "inventory_levels/update",
        howItStarts:
          "An inventory level changes in Shopify or the WMS, or a scheduled sync runs to reconcile inventory across all sales channels",
        setup:
          "Configure Shopify inventory webhooks and secondary channel API credentials. Set safety stock thresholds per SKU or globally.",
        inputFields: [],
      }),

      // 2. Shopify + secondary pull
      n("2", "runCode", "Pull inventory from all channels", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse the inventory event and prepare multi-channel data
const payload = vars.webhook_payload ? JSON.parse(vars.webhook_payload) : {};
const result = {
  sku: payload.sku || payload.inventory_item_id || "",
  shopify_qty: parseInt(payload.available || "0", 10),
  location_id: payload.location_id || "",
  product_id: payload.inventory_item_id || "",
  change_type: payload.available > 0 ? "restock" : "depletion",
  secondary_channel_qty: 0,
  needs_secondary_pull: "yes"
};
return result;`,
          outputVariables: [
            { name: "sku", type: "string" },
            { name: "shopify_qty", type: "number" },
            { name: "location_id", type: "string" },
            { name: "product_id", type: "string" },
            { name: "change_type", type: "string" },
            { name: "needs_secondary_pull", type: "string" },
          ],
        },
      }),

      // 3. Conflict detection (runCode)
      n("3", "runCode", "Detect inventory conflicts", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Detect conflicts between Shopify and secondary channel
const shopifyQty = parseInt(vars.shopify_qty || "0", 10);
const secondaryQty = parseInt(vars.secondary_channel_qty || "0", 10);
const safetyStock = parseInt(vars.safety_stock_threshold || "5", 10);
const diff = Math.abs(shopifyQty - secondaryQty);
const hasConflict = diff > 0;
const isOversell = shopifyQty < 0 || secondaryQty < 0;
const belowSafety = shopifyQty <= safetyStock || secondaryQty <= safetyStock;
const resolvedQty = Math.min(shopifyQty, secondaryQty);
const result = {
  has_conflict: hasConflict ? "yes" : "no",
  conflict_delta: diff,
  is_oversell: isOversell ? "yes" : "no",
  below_safety: belowSafety ? "yes" : "no",
  resolved_qty: resolvedQty,
  auto_resolvable: !isOversell && diff <= 10 ? "yes" : "no",
  safety_stock_threshold: safetyStock
};
return result;`,
          outputVariables: [
            { name: "has_conflict", type: "string" },
            { name: "conflict_delta", type: "number" },
            { name: "is_oversell", type: "string" },
            { name: "below_safety", type: "string" },
            { name: "resolved_qty", type: "number" },
            { name: "auto_resolvable", type: "string" },
            { name: "safety_stock_threshold", type: "number" },
          ],
        },
      }),

      // 4. Auto-resolve condition
      n("4", "condition", "Conflict auto-resolvable?", 760, {
        conditionData: [
          {
            variable: "auto_resolvable",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Inventory writeback
      n("5", "hitApi", "Writeback resolved inventory", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/inventory_levels/set.json",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "location_id", value: "{location_id}" },
            { key: "inventory_item_id", value: "{product_id}" },
            { key: "available", value: "{resolved_qty}" },
          ],
          variables: [
            {
              name: "inventory_level.available",
              customName: "final_available",
              type: "number",
            },
            {
              name: "inventory_level.inventory_item_id",
              customName: "synced_item_id",
              type: "string",
            },
          ],
        },
      }),

      // 6. Safety stock protection (runCode)
      n("6", "runCode", "Apply safety stock protection", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Enforce safety stock minimums
const finalQty = parseInt(vars.final_available || vars.resolved_qty || "0", 10);
const safetyThreshold = parseInt(vars.safety_stock_threshold || "5", 10);
const needsProtection = finalQty <= safetyThreshold;
const result = {
  protection_triggered: needsProtection ? "yes" : "no",
  final_quantity: finalQty,
  safety_buffer: Math.max(0, finalQty - safetyThreshold),
  action_taken: needsProtection ? "safety_stock_alert_sent" : "within_safe_levels",
  sync_status: "complete"
};
return result;`,
          outputVariables: [
            { name: "protection_triggered", type: "string" },
            { name: "final_quantity", type: "number" },
            { name: "safety_buffer", type: "number" },
            { name: "action_taken", type: "string" },
            { name: "sync_status", type: "string" },
          ],
        },
      }),

      // 7. Ops Slack alert
      slackNode(
        "7",
        "Slack: Inventory sync alert",
        1480,
        ":package: Inventory Sync Update\\nSKU: {sku} | Change: {change_type}\\nShopify: {shopify_qty} | Resolved: {resolved_qty} | Final: {final_quantity}\\nConflict: {has_conflict} (delta: {conflict_delta})\\nAuto-resolved: {auto_resolvable} | Oversell: {is_oversell}\\nSafety stock: {protection_triggered} (buffer: {safety_buffer})",
      ),

      // 8. Sync logging (Sheets)
      sheetsNode(
        "8",
        "Log inventory sync to Sheets",
        1720,
        "InventorySync",
        '[["{sku}","{shopify_qty}","{resolved_qty}","{final_quantity}","{has_conflict}","{conflict_delta}","{auto_resolvable}","{is_oversell}","{protection_triggered}","{sync_status}"]]',
      ),

      // 9. Oversell tracking
      n("9", "runCode", "Track oversell incidents", 1960, {
        runCodeDetails: {
          language: "javascript",
          code: `// Track and flag oversell incidents for ops review
const isOversell = vars.is_oversell === "yes";
const result = {
  oversell_flagged: isOversell ? "yes" : "no",
  incident_severity: isOversell ? "high" : "none",
  recommended_action: isOversell ? "manual_review_required" : "no_action",
  tracking_complete: "yes"
};
return result;`,
          outputVariables: [
            { name: "oversell_flagged", type: "string" },
            { name: "incident_severity", type: "string" },
            { name: "recommended_action", type: "string" },
            { name: "tracking_complete", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // auto-resolvable -> writeback
      e("4", "7", "output_2"), // not auto-resolvable -> ops alert
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 435. MULTI-ITEM CART RECOVERY (8 nodes)
  // ===================================================================

  "Multi-Item Cart Recovery": {
    summary:
      "Specialized recovery for carts with 3+ items. A multi-item cart abandon triggers cart analysis via runCode, detects bundle/threshold opportunities, generates AI multi-item pitch, sends a product-grid email, adds near-threshold incentives, follows up via SMS with item count, and logs cart-size performance to Sheets.",
    nodes: [
      trigger("Webhook: Multi-item cart abandon (3+)", "webhook_app", {
        source: "Shopify / E-commerce Platform",
        event: "checkouts/update (cart_item_count >= 3)",
        howItStarts:
          "A customer abandons a cart containing 3 or more items, qualifying for the multi-item recovery flow",
        setup:
          "Configure Shopify abandoned checkout webhook with filtering for carts with 3+ items. Include cart items, values, and customer details in the payload.",
        inputFields: [],
      }),

      // 2. Cart analysis (runCode)
      n("2", "runCode", "Analyze multi-item cart", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze multi-item cart for recovery strategy
const payload = vars.webhook_payload ? JSON.parse(vars.webhook_payload) : {};
const items = payload.line_items || [];
const cartTotal = items.reduce((sum, i) => sum + parseFloat(i.price || 0) * (i.quantity || 1), 0);
const itemCount = items.reduce((sum, i) => sum + (i.quantity || 1), 0);
const avgItemPrice = itemCount > 0 ? (cartTotal / itemCount).toFixed(2) : "0";
const freeShipThreshold = 75;
const nearThreshold = cartTotal >= freeShipThreshold * 0.8 && cartTotal < freeShipThreshold;
const categories = [...new Set(items.map(i => i.product_type || "general"))];
const topItems = items.slice(0, 5).map(i => i.title || i.name).join(", ");
const result = {
  cart_total: cartTotal.toFixed(2),
  item_count: itemCount,
  avg_item_price: avgItemPrice,
  near_free_shipping: nearThreshold ? "yes" : "no",
  shipping_gap: nearThreshold ? (freeShipThreshold - cartTotal).toFixed(2) : "0",
  categories: categories.join(", "),
  top_items: topItems,
  has_bundle_potential: categories.length <= 2 && itemCount >= 3 ? "yes" : "no",
  customer_email: payload.email || "",
  customer_name: (payload.billing_address || {}).first_name || "there",
  customer_phone: (payload.billing_address || {}).phone || "",
  cart_url: payload.abandoned_checkout_url || ""
};
return result;`,
          outputVariables: [
            { name: "cart_total", type: "string" },
            { name: "item_count", type: "number" },
            { name: "avg_item_price", type: "string" },
            { name: "near_free_shipping", type: "string" },
            { name: "shipping_gap", type: "string" },
            { name: "categories", type: "string" },
            { name: "top_items", type: "string" },
            { name: "has_bundle_potential", type: "string" },
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "customer_phone", type: "string" },
            { name: "cart_url", type: "string" },
          ],
        },
      }),

      // 3. Bundle/threshold detection
      n("3", "condition", "Near free-shipping threshold?", 520, {
        conditionData: [
          {
            variable: "near_free_shipping",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. AI multi-item pitch
      aiNode(
        "4",
        "AI generate multi-item pitch",
        760,
        "You are a cart recovery specialist for multi-item carts. Given the cart contents, generate a compelling recovery email that highlights the variety of items selected, creates a sense of curated collection, and emphasizes value. If near free-shipping threshold, prominently feature the gap amount. If bundle potential exists, suggest treating items as a set. Respond JSON: {subject_line: string, hero_headline: string, item_grid_intro: string, value_message: string, cta_text: string, add_on_suggestion: string, sms_message: string}",
        "Items: {top_items} | Count: {item_count} | Total: ${cart_total} | Avg price: ${avg_item_price} | Categories: {categories} | Near free shipping: {near_free_shipping} (gap: ${shipping_gap}) | Bundle potential: {has_bundle_potential} | Name: {customer_name}",
        [
          { field: "subject_line", as: "subject_line", type: "string" },
          { field: "hero_headline", as: "hero_headline", type: "string" },
          { field: "item_grid_intro", as: "item_grid_intro", type: "string" },
          { field: "value_message", as: "value_message", type: "string" },
          { field: "cta_text", as: "cta_text", type: "string" },
          {
            field: "add_on_suggestion",
            as: "add_on_suggestion",
            type: "string",
          },
          { field: "sms_message", as: "sms_message", type: "string" },
        ],
      ),

      // 5. Grid email
      emailNode(
        "5",
        "Send multi-item recovery email",
        1000,
        "{customer_email}",
        "{subject_line}",
        "{hero_headline}\\n\\n{item_grid_intro}\\n\\nYour curated selection ({item_count} items):\\n{top_items}\\n\\nCart total: ${cart_total}\\n\\n{value_message}\\n\\n{cta_text}: {cart_url}",
      ),

      // 6. Near-threshold add-on suggestion
      emailNode(
        "6",
        "Near-threshold add-on email",
        1000,
        "{customer_email}",
        "Just ${shipping_gap} away from FREE shipping!",
        "Hi {customer_name},\\n\\nYour cart of {item_count} items totals ${cart_total} — you're only ${shipping_gap} away from free shipping!\\n\\n{add_on_suggestion}\\n\\nComplete your purchase: {cart_url}",
        400,
      ),

      // 7. SMS with item-count (Twilio)
      smsNode(
        "7",
        "SMS cart reminder with item count",
        1240,
        "{customer_phone}",
        "{sms_message} - {item_count} items (${cart_total}) waiting: {cart_url}",
      ),

      // 8. Cart-size performance logging (Sheets)
      sheetsNode(
        "8",
        "Log cart-size performance to Sheets",
        1480,
        "MultiItemCartRecovery",
        '[["{customer_email}","{item_count}","{cart_total}","{avg_item_price}","{categories}","{near_free_shipping}","{shipping_gap}","{has_bundle_potential}","{subject_line}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "6", "output_1"), // near threshold -> add-on email
      e("3", "4", "output_2"), // not near threshold -> standard multi-item pitch
      e("4", "5"),
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 436. MULTI-PLATFORM PRODUCT LISTING (9 nodes)
  // ===================================================================

  "Multi-Platform Product Listing": {
    summary:
      "Syncs products to Google, Meta, and Amazon from a single source. A product create/update triggers canonical data pull, transforms and pushes to Google Shopping, Meta catalog, and Amazon, handles platform-specific errors via runCode, consolidates alerts on Slack, logs to a health dashboard on Sheets, and maintains field audit history.",
    nodes: [
      trigger("Webhook: Product create/update", "webhook_app", {
        source: "Shopify / PIM",
        event: "products/create | products/update",
        howItStarts:
          "A product is created or updated in the central catalog (Shopify or PIM), triggering distribution to Google, Meta, and Amazon",
        setup:
          "Configure Shopify product webhooks. Set up API credentials for Google Merchant Center, Meta Commerce, and Amazon SP-API.",
        inputFields: [],
      }),

      // 2. Canonical data pull
      shopifyNode(
        "2",
        "Pull canonical product data",
        280,
        "GET",
        "products/{product_id}.json",
        [],
        [
          {
            name: "product.title",
            customName: "product_title",
            type: "string",
          },
          {
            name: "product.body_html",
            customName: "product_description",
            type: "string",
          },
          {
            name: "product.vendor",
            customName: "product_brand",
            type: "string",
          },
          {
            name: "product.product_type",
            customName: "product_category",
            type: "string",
          },
          {
            name: "product.variants[0].price",
            customName: "product_price",
            type: "string",
          },
          {
            name: "product.variants[0].sku",
            customName: "product_sku",
            type: "string",
          },
          {
            name: "product.variants[0].barcode",
            customName: "product_gtin",
            type: "string",
          },
          {
            name: "product.images[0].src",
            customName: "product_image",
            type: "string",
          },
          {
            name: "product.handle",
            customName: "product_handle",
            type: "string",
          },
        ],
      ),

      // 3. Google transform + push
      n("3", "hitApi", "Push to Google Merchant Center", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://shoppingcontent.googleapis.com/content/v2.1/{merchant_id}/products",
          headers: [
            { key: "Authorization", value: "Bearer {google_merchant_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "offerId", value: "{product_sku}" },
            { key: "title", value: "{product_title}" },
            { key: "description", value: "{product_description}" },
            {
              key: "link",
              value:
                "https://{shopify_store}.myshopify.com/products/{product_handle}",
            },
            { key: "imageLink", value: "{product_image}" },
            {
              key: "price",
              value: '{"value":"{product_price}","currency":"USD"}',
            },
            { key: "brand", value: "{product_brand}" },
            { key: "gtin", value: "{product_gtin}" },
            { key: "channel", value: "online" },
            { key: "contentLanguage", value: "en" },
            { key: "targetCountry", value: "US" },
          ],
          variables: [
            { name: "id", customName: "google_product_id", type: "string" },
            { name: "kind", customName: "google_push_status", type: "string" },
          ],
        },
      }),

      // 4. Meta transform + push
      n(
        "4",
        "hitApi",
        "Push to Meta catalog",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://graph.facebook.com/v19.0/{meta_catalog_id}/products",
            headers: [
              { key: "Authorization", value: "Bearer {meta_access_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "retailer_id", value: "{product_sku}" },
              { key: "name", value: "{product_title}" },
              { key: "description", value: "{product_description}" },
              {
                key: "url",
                value:
                  "https://{shopify_store}.myshopify.com/products/{product_handle}",
              },
              { key: "image_url", value: "{product_image}" },
              { key: "price", value: "{product_price} USD" },
              { key: "brand", value: "{product_brand}" },
              { key: "availability", value: "in stock" },
            ],
            variables: [
              { name: "id", customName: "meta_product_id", type: "string" },
            ],
          },
        },
        400,
      ),

      // 5. Amazon transform + push
      n(
        "5",
        "hitApi",
        "Push to Amazon SP-API",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://sellingpartnerapi-na.amazon.com/listings/2021-08-01/items/{seller_id}/{product_sku}",
            headers: [
              { key: "x-amz-access-token", value: "{amazon_access_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "productType", value: "PRODUCT" },
              {
                key: "attributes",
                value: `{"item_name":[{"value":"{product_title}"}],"brand":[{"value":"{product_brand}"}],"externally_assigned_product_identifier":[{"type":"gtin","value":"{product_gtin}"}],"list_price":[{"value":"{product_price}","currency":"USD"}],"main_product_image_locator":[{"value":"{product_image}"}]}`,
              },
            ],
            variables: [
              {
                name: "status",
                customName: "amazon_push_status",
                type: "string",
              },
              {
                name: "submissionId",
                customName: "amazon_submission_id",
                type: "string",
              },
            ],
          },
        },
        720,
      ),

      // 6. Platform-specific error handling (runCode)
      n("6", "runCode", "Consolidate platform results", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Consolidate multi-platform push results
const googleOk = vars.google_product_id ? true : false;
const metaOk = vars.meta_product_id ? true : false;
const amazonOk = vars.amazon_push_status === "ACCEPTED" || vars.amazon_submission_id ? true : false;
const platforms = [];
const errors = [];
if (googleOk) platforms.push("Google"); else errors.push("Google: missing product ID");
if (metaOk) platforms.push("Meta"); else errors.push("Meta: missing product ID");
if (amazonOk) platforms.push("Amazon"); else errors.push("Amazon: " + (vars.amazon_push_status || "no response"));
const result = {
  platforms_succeeded: platforms.join(", "),
  platforms_failed: errors.length > 0 ? errors.join("; ") : "none",
  success_count: platforms.length,
  failure_count: errors.length,
  all_synced: errors.length === 0 ? "yes" : "no",
  sync_summary: platforms.length + "/3 platforms synced"
};
return result;`,
          outputVariables: [
            { name: "platforms_succeeded", type: "string" },
            { name: "platforms_failed", type: "string" },
            { name: "success_count", type: "number" },
            { name: "failure_count", type: "number" },
            { name: "all_synced", type: "string" },
            { name: "sync_summary", type: "string" },
          ],
        },
      }),

      // 7. Consolidated alert (Slack)
      slackNode(
        "7",
        "Slack: Multi-platform sync result",
        1000,
        ":globe_with_meridians: Multi-Platform Sync\\nProduct: {product_title} (SKU: {product_sku})\\nResult: {sync_summary}\\nSucceeded: {platforms_succeeded}\\nFailed: {platforms_failed}\\nGoogle ID: {google_product_id}\\nMeta ID: {meta_product_id}\\nAmazon: {amazon_push_status}",
      ),

      // 8. Health dashboard (Sheets)
      sheetsNode(
        "8",
        "Log to health dashboard",
        1240,
        "MultiPlatformSync",
        '[["{product_sku}","{product_title}","{success_count}","{failure_count}","{platforms_succeeded}","{platforms_failed}","{google_product_id}","{meta_product_id}","{amazon_push_status}"]]',
      ),

      // 9. Field audit history
      sheetsNode(
        "9",
        "Log field audit history",
        1480,
        "FieldAuditHistory",
        '[["{product_sku}","{product_title}","{product_price}","{product_brand}","{product_gtin}","{product_category}","{all_synced}","{sync_summary}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("2", "4"),
      e("2", "5"),
      e("3", "6"),
      e("4", "6"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 437. NEW PRODUCT CATALOG SYNC TO GOOGLE SHOPPING (8 nodes)
  // ===================================================================

  "New Product Catalog Sync to Google Shopping": {
    summary:
      "Syncs new Shopify products to Google Merchant Center with AI category suggestions. A new product webhook triggers full data extraction, AI suggests Google product categories, validates images and attributes, publishes via Merchant API, monitors approval status, alerts catalog ops on Slack, and logs to Sheets.",
    nodes: [
      trigger("Webhook: New Shopify product", "webhook_app", {
        source: "Shopify",
        event: "products/create",
        howItStarts:
          "A new product is created in Shopify, triggering automatic listing creation in Google Shopping / Merchant Center",
        setup:
          "Configure Shopify webhook for products/create. Set up Google Merchant Center API credentials and merchant ID.",
        inputFields: [],
      }),

      // 2. Full product extraction
      shopifyNode(
        "2",
        "Extract full product details",
        280,
        "GET",
        "products/{product_id}.json",
        [],
        [
          {
            name: "product.title",
            customName: "product_title",
            type: "string",
          },
          {
            name: "product.body_html",
            customName: "product_description_html",
            type: "string",
          },
          {
            name: "product.vendor",
            customName: "product_brand",
            type: "string",
          },
          {
            name: "product.product_type",
            customName: "product_type",
            type: "string",
          },
          { name: "product.tags", customName: "product_tags", type: "string" },
          {
            name: "product.variants[0].price",
            customName: "product_price",
            type: "string",
          },
          {
            name: "product.variants[0].sku",
            customName: "product_sku",
            type: "string",
          },
          {
            name: "product.variants[0].barcode",
            customName: "product_gtin",
            type: "string",
          },
          {
            name: "product.variants[0].weight",
            customName: "product_weight",
            type: "number",
          },
          {
            name: "product.images[0].src",
            customName: "product_image",
            type: "string",
          },
          {
            name: "product.images.length",
            customName: "image_count",
            type: "number",
          },
          {
            name: "product.handle",
            customName: "product_handle",
            type: "string",
          },
        ],
      ),

      // 3. AI category suggestion
      aiNode(
        "3",
        "AI suggest Google product category",
        520,
        "You are a Google Shopping product taxonomy specialist. Given a product title, description, type, tags, and brand, suggest the most accurate Google product category ID and taxonomy path. Also generate an optimized title (max 150 chars) and clean description (no HTML, max 5000 chars) for Google Shopping. Respond JSON: {google_category_id: string, google_category_path: string, optimized_title: string, clean_description: string, additional_attributes: string, confidence: number}",
        "Title: {product_title} | Type: {product_type} | Brand: {product_brand} | Tags: {product_tags} | Description: {product_description_html}",
        [
          {
            field: "google_category_id",
            as: "google_category_id",
            type: "string",
          },
          {
            field: "google_category_path",
            as: "google_category_path",
            type: "string",
          },
          { field: "optimized_title", as: "optimized_title", type: "string" },
          {
            field: "clean_description",
            as: "clean_description",
            type: "string",
          },
          { field: "confidence", as: "category_confidence", type: "number" },
        ],
      ),

      // 4. Image/attribute validation condition
      n("4", "condition", "Product data meets Google requirements?", 760, {
        conditionData: [
          {
            variable: "product_image",
            value: "",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
          {
            variable: "product_price",
            value: "0",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
          {
            variable: "product_title",
            value: "",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Merchant API publish
      n("5", "hitApi", "Publish to Google Merchant Center", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://shoppingcontent.googleapis.com/content/v2.1/{merchant_id}/products",
          headers: [
            { key: "Authorization", value: "Bearer {google_merchant_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "offerId", value: "{product_sku}" },
            { key: "title", value: "{optimized_title}" },
            { key: "description", value: "{clean_description}" },
            {
              key: "link",
              value:
                "https://{shopify_store}.myshopify.com/products/{product_handle}",
            },
            { key: "imageLink", value: "{product_image}" },
            {
              key: "price",
              value: '{"value":"{product_price}","currency":"USD"}',
            },
            { key: "brand", value: "{product_brand}" },
            { key: "gtin", value: "{product_gtin}" },
            { key: "googleProductCategory", value: "{google_category_id}" },
            { key: "channel", value: "online" },
            { key: "contentLanguage", value: "en" },
            { key: "targetCountry", value: "US" },
            { key: "availability", value: "in stock" },
            { key: "condition", value: "new" },
          ],
          variables: [
            { name: "id", customName: "google_listing_id", type: "string" },
            { name: "kind", customName: "listing_kind", type: "string" },
          ],
        },
      }),

      // 6. Approval monitoring
      n("6", "hitApi", "Check product approval status", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://shoppingcontent.googleapis.com/content/v2.1/{merchant_id}/productstatuses/{google_listing_id}",
          headers: [
            { key: "Authorization", value: "Bearer {google_merchant_token}" },
          ],
          body: [],
          variables: [
            {
              name: "destinationStatuses[0].status",
              customName: "approval_status",
              type: "string",
            },
            {
              name: "itemLevelIssues.length",
              customName: "issue_count",
              type: "number",
            },
          ],
        },
      }),

      // 7. Catalog ops Slack alert
      slackNode(
        "7",
        "Slack: Google Shopping sync alert",
        1480,
        ":shopping_bags: Google Shopping Sync\\nProduct: {optimized_title} (SKU: {product_sku})\\nCategory: {google_category_path} (confidence: {category_confidence}%)\\nListing ID: {google_listing_id}\\nApproval: {approval_status} | Issues: {issue_count}\\nPrice: ${product_price} | Images: {image_count}",
      ),

      // 8. Sync logging (Sheets)
      sheetsNode(
        "8",
        "Log sync to Sheets",
        1720,
        "GoogleShoppingSync",
        '[["{product_sku}","{optimized_title}","{product_brand}","{google_category_id}","{google_category_path}","{category_confidence}","{google_listing_id}","{approval_status}","{issue_count}","{product_price}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // valid -> publish
      e("4", "7", "output_2"), // invalid -> alert ops
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 438. NEW-ARRIVAL ALERT TO RECENT PURCHASERS (7 nodes)
  // ===================================================================

  "New-Arrival Alert to Recent Purchasers": {
    summary:
      "Notifies recent purchasers about new arrivals matching their category affinity. A new product publication triggers category affinity segment pull from HubSpot, generates AI personalized messaging, routes on early-access incentive eligibility, sends new-arrival emails, tracks clicks and conversions, and logs category cohort performance to Sheets.",
    nodes: [
      trigger("Webhook: New product published", "webhook_app", {
        source: "Shopify",
        event: "products/create",
        howItStarts:
          "A new product is published in the store, triggering targeted alerts to recent purchasers who bought from the same category",
        setup:
          "Configure Shopify webhook for new product creation. Set up HubSpot for segment retrieval by purchase category affinity.",
        inputFields: [],
      }),

      // 2. Category affinity segment pull (HubSpot)
      hubspotNode(
        "2",
        "Pull category affinity segment",
        280,
        "POST",
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"last_purchase_category","operator":"EQ","value":"{product_category}"},{"propertyName":"last_purchase_date","operator":"GTE","value":"{ninety_days_ago}"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","last_purchase_category","lifetime_value","last_purchase_date"]',
          },
          { key: "limit", value: "100" },
        ],
        [
          { name: "total", customName: "segment_size", type: "number" },
          { name: "results", customName: "affinity_contacts", type: "string" },
        ],
      ),

      // 3. AI personalized messaging
      aiNode(
        "3",
        "AI personalize new-arrival message",
        520,
        "You are a retail marketing personalization expert. Given a new product and a segment of category-affinity customers, generate personalized new-arrival messaging that connects their past purchase interest to the new product. Create an email subject, headline, product narrative, and a personalized CTA. Include early-access framing. Respond JSON: {subject_line: string, headline: string, product_narrative: string, personalized_cta: string, early_access_pitch: string, incentive_recommendation: string}",
        "New product: {product_title} | Category: {product_category} | Price: ${product_price} | Brand: {product_brand} | Segment size: {segment_size} | Segment affinity: {product_category}",
        [
          { field: "subject_line", as: "subject_line", type: "string" },
          { field: "headline", as: "headline", type: "string" },
          {
            field: "product_narrative",
            as: "product_narrative",
            type: "string",
          },
          { field: "personalized_cta", as: "personalized_cta", type: "string" },
          {
            field: "early_access_pitch",
            as: "early_access_pitch",
            type: "string",
          },
          {
            field: "incentive_recommendation",
            as: "incentive_recommendation",
            type: "string",
          },
        ],
      ),

      // 4. Early-access incentive condition
      n("4", "condition", "VIP early-access eligible?", 760, {
        conditionData: [
          {
            variable: "segment_size",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. New-arrival email
      emailNode(
        "5",
        "Send new-arrival alert email",
        1000,
        "{contact_email}",
        "{subject_line}",
        "{headline}\\n\\n{early_access_pitch}\\n\\n{product_narrative}\\n\\nPrice: ${product_price}\\n\\n{personalized_cta}\\n\\nAs a valued customer who loves {product_category}, you're getting first access to this new arrival.",
      ),

      // 6. Click/conversion tracking
      n("6", "hitApi", "Track new-arrival email engagement", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{analytics_api}/api/v1/events/email-engagement",
          headers: [
            { key: "Authorization", value: "Bearer {analytics_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "campaign", value: "new_arrival_alert" },
            { key: "product_id", value: "{product_id}" },
            { key: "segment", value: "{product_category}" },
            { key: "segment_size", value: "{segment_size}" },
          ],
          variables: [
            {
              name: "tracked",
              customName: "tracking_confirmed",
              type: "boolean",
            },
          ],
        },
      }),

      // 7. Category cohort logging (Sheets)
      sheetsNode(
        "7",
        "Log category cohort performance",
        1480,
        "NewArrivalAlerts",
        '[["{product_title}","{product_category}","{product_price}","{segment_size}","{subject_line}","{incentive_recommendation}","{tracking_confirmed}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // has matching segment -> send email
      e("4", "7", "output_2"), // no matching segment -> log only
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 439. NEW-ARRIVALS REACTIVATION (8 nodes)
  // ===================================================================

  "New-Arrivals Reactivation": {
    summary:
      "Reactivates lapsed customers by matching new arrivals to their category affinity. A lapsed customer scan identifies dormant shoppers, matches their category preferences to new Shopify arrivals, generates AI reactivation copy, optionally adds a return incentive, sends reactivation email, follows up via SMS for non-openers, and logs performance to Sheets.",
    nodes: [
      trigger("Schedule: Lapsed customer reactivation scan", "schedule", {
        schedule: "Daily at 10:00 AM UTC",
        howItStarts:
          "A daily scheduled scan identifies customers who haven't purchased in 60-120 days and matches them with relevant new arrivals",
        setup:
          "Configure customer lapse threshold (default 60 days). Ensure Shopify has recently-published products. Set up Twilio for SMS follow-up.",
        inputFields: [],
      }),

      // 2. Category affinity matching
      aiNode(
        "2",
        "AI match lapsed customers to categories",
        280,
        "You are a customer reactivation specialist. Given lapsed customer data, identify their category affinities based on past purchase history. Group customers by their primary product category interest and determine reactivation priority (high/medium/low based on recency and lifetime value). Respond JSON: {customer_email: string, customer_name: string, customer_phone: string, primary_category: string, secondary_category: string, days_since_purchase: number, lifetime_value: string, reactivation_priority: string, incentive_eligible: string}",
        "Lapsed customers from CRM scan with purchase history. Customer data: {webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_phone", as: "customer_phone", type: "string" },
          { field: "primary_category", as: "primary_category", type: "string" },
          {
            field: "days_since_purchase",
            as: "days_since_purchase",
            type: "number",
          },
          { field: "lifetime_value", as: "lifetime_value", type: "string" },
          {
            field: "reactivation_priority",
            as: "reactivation_priority",
            type: "string",
          },
          {
            field: "incentive_eligible",
            as: "incentive_eligible",
            type: "string",
          },
        ],
      ),

      // 3. New arrivals pull (Shopify)
      shopifyNode(
        "3",
        "Pull new arrivals from Shopify",
        520,
        "GET",
        "products.json?created_at_min={seven_days_ago}&status=active&limit=50",
        [],
        [
          {
            name: "products.length",
            customName: "new_arrival_count",
            type: "number",
          },
          {
            name: "products[0].title",
            customName: "top_arrival_title",
            type: "string",
          },
          {
            name: "products[0].variants[0].price",
            customName: "top_arrival_price",
            type: "string",
          },
          {
            name: "products[0].images[0].src",
            customName: "top_arrival_image",
            type: "string",
          },
        ],
      ),

      // 4. AI reactivation copy
      aiNode(
        "4",
        "AI generate reactivation copy",
        760,
        "You are a win-back email copywriter. Given a lapsed customer profile and matching new arrivals, write a warm reactivation email that acknowledges the customer's absence, highlights new products in their interest category, and creates excitement to return. If incentive eligible, weave in a welcome-back offer. Respond JSON: {subject_line: string, greeting: string, reactivation_body: string, product_highlight: string, incentive_copy: string, cta_text: string, sms_reactivation: string}",
        "Customer: {customer_name} | Email: {customer_email} | Days lapsed: {days_since_purchase} | Category: {primary_category} | LTV: {lifetime_value} | Priority: {reactivation_priority} | Incentive eligible: {incentive_eligible} | New arrivals: {new_arrival_count} | Top arrival: {top_arrival_title} (${top_arrival_price})",
        [
          { field: "subject_line", as: "subject_line", type: "string" },
          { field: "greeting", as: "greeting", type: "string" },
          {
            field: "reactivation_body",
            as: "reactivation_body",
            type: "string",
          },
          {
            field: "product_highlight",
            as: "product_highlight",
            type: "string",
          },
          { field: "incentive_copy", as: "incentive_copy", type: "string" },
          { field: "cta_text", as: "cta_text", type: "string" },
          { field: "sms_reactivation", as: "sms_reactivation", type: "string" },
        ],
      ),

      // 5. Return incentive condition
      n("5", "condition", "Incentive allowed for customer?", 1000, {
        conditionData: [
          {
            variable: "incentive_eligible",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Reactivation email
      emailNode(
        "6",
        "Send reactivation email",
        1240,
        "{customer_email}",
        "{subject_line}",
        "{greeting}\\n\\n{reactivation_body}\\n\\n{product_highlight}\\n\\n{incentive_copy}\\n\\n{cta_text}",
      ),

      // 7. SMS for non-openers (Twilio)
      smsNode(
        "7",
        "SMS reactivation for non-openers",
        1480,
        "{customer_phone}",
        "{sms_reactivation}",
      ),

      // 8. Reactivation logging (Sheets)
      sheetsNode(
        "8",
        "Log reactivation to Sheets",
        1720,
        "NewArrivalReactivation",
        '[["{customer_email}","{customer_name}","{days_since_purchase}","{primary_category}","{lifetime_value}","{reactivation_priority}","{incentive_eligible}","{new_arrival_count}","{subject_line}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // incentive eligible -> send with incentive
      e("5", "6", "output_2"), // not eligible -> send without incentive
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 440. NO-DISCOUNT CART RECOVERY (8 nodes)
  // ===================================================================

  "No-Discount Cart Recovery": {
    summary:
      "Recovers abandoned carts using value-led messaging without discounts. A cart abandon triggers a value-led reminder email, checks engagement after a delay, sends social proof email to engaged users, waits again, sends trust/guarantee email, suppresses purchasers, and logs no-discount vs discount comparison to Sheets.",
    nodes: [
      trigger("Webhook: Cart abandon event", "webhook_app", {
        source: "Shopify",
        event: "checkouts/update (status: abandoned)",
        howItStarts:
          "A customer abandons their cart, entering the no-discount recovery sequence that uses value, social proof, and trust instead of coupons",
        setup:
          "Configure Shopify abandoned checkout webhook. This workflow intentionally avoids discounts — uses value props, social proof, and guarantees instead.",
        inputFields: [],
      }),

      // 2. Value-led reminder email
      emailNode(
        "2",
        "Send value-led reminder email",
        280,
        "{customer_email}",
        "Still thinking about it? Here's why customers love {cart_primary_item}",
        "Hi {customer_name},\\n\\nYou left {cart_primary_item} in your cart.\\n\\nHere's what makes it special:\\n- Premium quality materials\\n- Designed to last\\n- Free returns within 30 days\\n\\nCart total: {cart_value}\\n\\nComplete your purchase: {cart_url}",
      ),

      // 3. Engagement delay + condition
      n("3", "delayAction", "Wait 24 hours", 520, {
        delayDetails: { delayType: "fixed", days: 1, hours: 0, minutes: 0 },
      }),

      // 4. Social proof email (condition: not purchased)
      n("4", "condition", "Still not purchased?", 760, {
        conditionData: [
          {
            variable: "purchased",
            value: "true",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Social proof email
      emailNode(
        "5",
        "Send social proof email",
        1000,
        "{customer_email}",
        "{cart_primary_item} is popular right now - {recent_buyers} bought this week",
        "Hi {customer_name},\\n\\n{cart_primary_item} is one of our best sellers this week.\\n\\n{recent_buyers} other customers purchased it in the past 7 days.\\n\\nAverage rating: {product_rating}/5 stars\\n\\nDon't miss out — your cart is still saved: {cart_url}",
      ),

      // 6. Engagement delay + second condition
      n("6", "delayAction", "Wait 48 more hours", 1240, {
        delayDetails: { delayType: "fixed", days: 2, hours: 0, minutes: 0 },
      }),

      // 7. Trust/guarantee email
      emailNode(
        "7",
        "Send trust and guarantee email",
        1480,
        "{customer_email}",
        "Shop with confidence: Our guarantee on {cart_primary_item}",
        "Hi {customer_name},\\n\\nWe want you to feel great about your purchase.\\n\\nOur promises:\\n- 30-day hassle-free returns\\n- Free shipping on all orders\\n- 1-year warranty included\\n- Secure checkout with fraud protection\\n\\nYour cart ({cart_value}) is ready: {cart_url}\\n\\nQuestions? Reply to this email and we'll help.",
      ),

      // 8. No-discount vs discount comparison logging (Sheets)
      sheetsNode(
        "8",
        "Log no-discount performance to Sheets",
        1720,
        "NoDiscountRecovery",
        '[["{customer_email}","{cart_value}","{cart_primary_item}","{purchased}","no_discount","value_social_trust","{recent_buyers}","{product_rating}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // not purchased -> social proof
      e("4", "8", "output_2"), // purchased -> log and stop
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 441. ONBOARDING TIPS AFTER SOFTWARE PURCHASE (8 nodes)
  // ===================================================================

  "Onboarding Tips after Software Purchase": {
    summary:
      "Guides new software customers from purchase to activation. A software purchase triggers license delivery, AI generates a personalized setup guide, sends day-1 onboarding email, waits 3 days, checks activation status, creates a proactive support ticket in Zendesk for non-activated users, and logs time-to-activation to Sheets.",
    nodes: [
      trigger("Webhook: Software purchase completed", "webhook_app", {
        source: "Stripe / E-commerce Platform",
        event: "checkout.session.completed",
        howItStarts:
          "A customer completes a software purchase, triggering the onboarding and activation sequence",
        setup:
          "Configure Stripe webhook for checkout.session.completed (or your payment platform equivalent). Include product details, license info, and customer data in the payload.",
        inputFields: [],
      }),

      // 2. License delivery
      n("2", "hitApi", "Generate and deliver license key", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://{license_api}/api/v1/licenses/generate",
          headers: [
            { key: "Authorization", value: "Bearer {license_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "product_id", value: "{product_id}" },
            { key: "customer_email", value: "{customer_email}" },
            { key: "plan", value: "{plan_name}" },
          ],
          variables: [
            { name: "license_key", customName: "license_key", type: "string" },
            {
              name: "download_url",
              customName: "download_url",
              type: "string",
            },
            {
              name: "expires_at",
              customName: "license_expiry",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI personalized setup guide
      aiNode(
        "3",
        "AI generate personalized setup guide",
        520,
        "You are a software onboarding specialist. Given the product purchased, plan tier, and customer context, generate a personalized setup guide with step-by-step instructions, quick-start tips, and common first-day tasks. Tailor complexity to the plan tier. Respond JSON: {setup_steps: string, quick_start_tips: string, first_day_tasks: string, video_tutorial_link: string, estimated_setup_time: string, personalized_greeting: string}",
        "Product: {product_name} | Plan: {plan_name} | Customer: {customer_name} | Email: {customer_email} | License: {license_key}",
        [
          { field: "setup_steps", as: "setup_steps", type: "string" },
          { field: "quick_start_tips", as: "quick_start_tips", type: "string" },
          { field: "first_day_tasks", as: "first_day_tasks", type: "string" },
          {
            field: "estimated_setup_time",
            as: "estimated_setup_time",
            type: "string",
          },
          {
            field: "personalized_greeting",
            as: "personalized_greeting",
            type: "string",
          },
        ],
      ),

      // 4. Day-1 onboarding email
      emailNode(
        "4",
        "Send day-1 onboarding email",
        760,
        "{customer_email}",
        "Welcome to {product_name}! Let's get you started",
        "{personalized_greeting}\\n\\nYour license key: {license_key}\\nDownload: {download_url}\\n\\nEstimated setup time: {estimated_setup_time}\\n\\nQuick Start:\\n{quick_start_tips}\\n\\nSetup Steps:\\n{setup_steps}\\n\\nYour First Day Tasks:\\n{first_day_tasks}\\n\\nNeed help? Reply to this email or visit our support center.",
      ),

      // 5. Day-3 progress check delay
      n("5", "delayAction", "Wait 3 days for activation check", 1000, {
        delayDetails: { delayType: "fixed", days: 3, hours: 0, minutes: 0 },
      }),

      // 6. Activation check condition
      n("6", "condition", "User activated product?", 1240, {
        conditionData: [
          {
            variable: "activated",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Proactive support ticket (Zendesk) — non-activated
      zendeskNode(
        "7",
        "Create proactive support ticket",
        1480,
        "Onboarding Help Needed: {customer_name} - {product_name}",
        "Customer {customer_name} ({customer_email}) purchased {product_name} ({plan_name}) 3 days ago but has not activated their account.\\n\\nLicense: {license_key}\\nPurchase date: {purchase_date}\\nActivation status: Not activated\\n\\nPlease reach out proactively to assist with onboarding.",
        "normal",
        [
          {
            name: "ticket.id",
            customName: "support_ticket_id",
            type: "string",
          },
          {
            name: "ticket.status",
            customName: "ticket_status",
            type: "string",
          },
        ],
      ),

      // 8. Time-to-activation logging (Sheets)
      sheetsNode(
        "8",
        "Log time-to-activation to Sheets",
        1720,
        "SoftwareOnboarding",
        '[["{customer_email}","{customer_name}","{product_name}","{plan_name}","{license_key}","{activated}","{support_ticket_id}","{estimated_setup_time}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "8", "output_1"), // activated -> log directly
      e("6", "7", "output_2"), // not activated -> create support ticket
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 442. ONE-ITEM CART RECOVERY (7 nodes)
  // ===================================================================

  "One-Item Cart Recovery": {
    summary:
      "Specialized recovery for single-item cart abandons using editorial storytelling. A single-item cart abandon triggers deep product details pull from Shopify, AI generates editorial narrative copy, sends a storytelling email, includes close alternatives, follows up via SMS with a direct link, and logs single vs multi-item comparison to Sheets.",
    nodes: [
      trigger("Webhook: Single-item cart abandon", "webhook_app", {
        source: "Shopify",
        event: "checkouts/update (item_count: 1)",
        howItStarts:
          "A customer abandons a cart containing exactly one item, triggering the single-item editorial recovery flow",
        setup:
          "Configure Shopify abandoned checkout webhook with filtering for single-item carts. Include full product details in the payload.",
        inputFields: [],
      }),

      // 2. Deep product details pull (Shopify)
      shopifyNode(
        "2",
        "Pull deep product details",
        280,
        "GET",
        "products/{product_id}.json",
        [],
        [
          {
            name: "product.title",
            customName: "product_title",
            type: "string",
          },
          {
            name: "product.body_html",
            customName: "product_description",
            type: "string",
          },
          {
            name: "product.vendor",
            customName: "product_brand",
            type: "string",
          },
          {
            name: "product.product_type",
            customName: "product_category",
            type: "string",
          },
          { name: "product.tags", customName: "product_tags", type: "string" },
          {
            name: "product.variants[0].price",
            customName: "product_price",
            type: "string",
          },
          {
            name: "product.images[0].src",
            customName: "product_image",
            type: "string",
          },
          {
            name: "product.variants[0].inventory_quantity",
            customName: "stock_level",
            type: "number",
          },
        ],
      ),

      // 3. AI editorial narrative
      aiNode(
        "3",
        "AI generate editorial narrative",
        520,
        "You are a luxury editorial copywriter. Given a single product, write a compelling editorial-style recovery email that tells the product's story — materials, craftsmanship, design philosophy, how it fits into the customer's life. Make it feel like a magazine feature, not a sales pitch. Also suggest 2-3 close alternatives. Respond JSON: {subject_line: string, editorial_headline: string, product_story: string, craftsmanship_note: string, lifestyle_angle: string, alternatives_text: string, sms_direct_message: string}",
        "Product: {product_title} | Price: ${product_price} | Brand: {product_brand} | Category: {product_category} | Description: {product_description} | Tags: {product_tags} | Stock: {stock_level} | Customer: {customer_name}",
        [
          { field: "subject_line", as: "subject_line", type: "string" },
          {
            field: "editorial_headline",
            as: "editorial_headline",
            type: "string",
          },
          { field: "product_story", as: "product_story", type: "string" },
          {
            field: "craftsmanship_note",
            as: "craftsmanship_note",
            type: "string",
          },
          { field: "lifestyle_angle", as: "lifestyle_angle", type: "string" },
          {
            field: "alternatives_text",
            as: "alternatives_text",
            type: "string",
          },
          {
            field: "sms_direct_message",
            as: "sms_direct_message",
            type: "string",
          },
        ],
      ),

      // 4. Storytelling email
      emailNode(
        "4",
        "Send editorial storytelling email",
        760,
        "{customer_email}",
        "{subject_line}",
        "{editorial_headline}\\n\\n{product_story}\\n\\n{craftsmanship_note}\\n\\n{lifestyle_angle}\\n\\n{product_title} - ${product_price}\\n\\nComplete your purchase: {cart_url}",
      ),

      // 5. Close alternatives inclusion
      emailNode(
        "5",
        "Send alternatives follow-up",
        1000,
        "{customer_email}",
        "More like {product_title} you might love",
        "Hi {customer_name},\\n\\nStill considering {product_title}? Here are similar options you might also enjoy:\\n\\n{alternatives_text}\\n\\nOr go back to your original choice: {cart_url}",
      ),

      // 6. SMS direct link (Twilio)
      smsNode(
        "6",
        "SMS with direct product link",
        1240,
        "{customer_phone}",
        "{sms_direct_message}",
      ),

      // 7. Single vs multi-item comparison logging (Sheets)
      sheetsNode(
        "7",
        "Log single-item recovery to Sheets",
        1480,
        "SingleItemRecovery",
        '[["{customer_email}","{product_title}","{product_price}","{product_brand}","{product_category}","single_item","{stock_level}","{subject_line}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 443. ORDER CANCELLATION AND REFUND BOT (8 nodes)
  // ===================================================================

  "Order Cancellation and Refund Bot": {
    summary:
      "Handles order cancellations with fulfillment-state awareness. A cancellation request triggers fulfillment state check, offers pre-shipment salvage, executes cancel/refund via Shopify and Stripe for pre-shipment orders, redirects post-shipment to returns, sends confirmation email, logs reasons to Sheets, and escalates edge cases to Slack.",
    nodes: [
      trigger("Webhook: Order cancellation request", "webhook_app", {
        source: "Customer Portal / Support System",
        event: "order.cancellation.requested",
        howItStarts:
          "A customer requests to cancel an order through the portal, email, or support channel",
        setup:
          "Connect your customer portal or support system to POST cancellation requests (order ID, customer email, reason) to the workflow URL. Set up Shopify and Stripe credentials for cancel/refund execution.",
        inputFields: [],
      }),

      // 2. Fulfillment state check condition
      n("2", "hitApi", "Check order fulfillment state", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/orders/{order_id}.json",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
          ],
          body: [],
          variables: [
            {
              name: "order.fulfillment_status",
              customName: "fulfillment_status",
              type: "string",
            },
            {
              name: "order.financial_status",
              customName: "financial_status",
              type: "string",
            },
            {
              name: "order.total_price",
              customName: "order_total",
              type: "string",
            },
            {
              name: "order.email",
              customName: "customer_email",
              type: "string",
            },
            { name: "order.name", customName: "order_name", type: "string" },
            {
              name: "order.line_items[0].title",
              customName: "primary_item",
              type: "string",
            },
          ],
        },
      }),

      // 3. Pre-shipment vs post-shipment condition
      n("3", "condition", "Order not yet shipped?", 520, {
        conditionData: [
          {
            variable: "fulfillment_status",
            value: "fulfilled",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Cancel + refund execution (Shopify/Stripe) — pre-shipment
      n("4", "hitApi", "Cancel order and process refund", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/orders/{order_id}/cancel.json",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "reason", value: "customer" },
            { key: "restock", value: "true" },
            { key: "email", value: "true" },
          ],
          variables: [
            {
              name: "order.cancelled_at",
              customName: "cancelled_at",
              type: "string",
            },
            {
              name: "order.cancel_reason",
              customName: "cancel_reason",
              type: "string",
            },
          ],
        },
      }),

      // 5. Post-shipment return redirect
      emailNode(
        "5",
        "Redirect to returns process",
        760,
        "{customer_email}",
        "Your Order {order_name} Has Shipped - Return Instructions",
        "Hi,\\n\\nYour order {order_name} ({primary_item}) has already shipped and cannot be cancelled.\\n\\nTo return the item, please:\\n1. Wait for delivery\\n2. Visit our returns portal: https://{shopify_store}.myshopify.com/returns\\n3. Enter order {order_name} to start your return\\n\\nRefunds are processed within 5-7 business days of receiving the return.\\n\\nWe apologize for the inconvenience.",
        400,
      ),

      // 6. Confirmation email
      emailNode(
        "6",
        "Send cancellation confirmation email",
        1000,
        "{customer_email}",
        "Order {order_name} Cancelled - Refund Processed",
        "Hi,\\n\\nYour order {order_name} has been successfully cancelled.\\n\\nItem: {primary_item}\\nOrder total: ${order_total}\\nRefund: Full refund of ${order_total} will appear within 5-10 business days.\\n\\nCancellation time: {cancelled_at}\\nReason recorded: {cancel_reason}\\n\\nWe're sorry to see your order go. If there's anything we can improve, please let us know.",
      ),

      // 7. Reason logging (Sheets)
      sheetsNode(
        "7",
        "Log cancellation reason to Sheets",
        1240,
        "OrderCancellations",
        '[["{order_name}","{customer_email}","{primary_item}","{order_total}","{fulfillment_status}","{cancel_reason}","{cancelled_at}","{financial_status}"]]',
      ),

      // 8. Edge case escalation (Slack)
      slackNode(
        "8",
        "Slack: Edge case escalation",
        1480,
        ":warning: Order Cancellation Processed\\nOrder: {order_name} | Total: ${order_total}\\nItem: {primary_item}\\nFulfillment: {fulfillment_status} | Financial: {financial_status}\\nReason: {cancel_reason}\\nCustomer: {customer_email}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // not shipped -> cancel + refund
      e("3", "5", "output_2"), // shipped -> return redirect
      e("4", "6"),
      e("5", "7"), // post-shipment returns also logged
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 444. ORDER CONFIRMATION (7 nodes)
  // ===================================================================

  "Order Confirmation": {
    summary:
      "Sends branded order confirmation across email and SMS. An order webhook triggers full order and customer data pull from Shopify, AI generates branded confirmation copy, sends confirmation email, sends SMS confirmation via Twilio, updates the CRM in HubSpot, and logs delivery status to Sheets.",
    nodes: [
      trigger("Webhook: New order placed", "webhook_app", {
        source: "Shopify",
        event: "orders/create",
        howItStarts:
          "A new order is placed in Shopify, triggering branded confirmation across email and SMS channels",
        setup:
          "Configure Shopify webhook for orders/create. Set up Twilio for SMS and HubSpot for CRM updates.",
        inputFields: [],
      }),

      // 2. Order/customer pull (Shopify)
      shopifyNode(
        "2",
        "Pull full order and customer details",
        280,
        "GET",
        "orders/{order_id}.json",
        [],
        [
          { name: "order.name", customName: "order_name", type: "string" },
          { name: "order.email", customName: "customer_email", type: "string" },
          {
            name: "order.total_price",
            customName: "order_total",
            type: "string",
          },
          {
            name: "order.line_items[0].title",
            customName: "primary_item",
            type: "string",
          },
          {
            name: "order.line_items.length",
            customName: "item_count",
            type: "number",
          },
          {
            name: "order.shipping_address.first_name",
            customName: "customer_name",
            type: "string",
          },
          {
            name: "order.shipping_address.phone",
            customName: "customer_phone",
            type: "string",
          },
          {
            name: "order.shipping_address.city",
            customName: "shipping_city",
            type: "string",
          },
          {
            name: "order.created_at",
            customName: "order_date",
            type: "string",
          },
          {
            name: "order.customer.id",
            customName: "shopify_customer_id",
            type: "string",
          },
        ],
      ),

      // 3. AI branded confirmation
      aiNode(
        "3",
        "AI generate branded confirmation",
        520,
        "You are a brand voice copywriter for order confirmations. Generate a warm, branded confirmation message with a personalized greeting, order summary highlight, estimated delivery note, and a brief brand-aligned closing. Keep it concise and joyful. Respond JSON: {email_subject: string, greeting: string, order_highlight: string, delivery_note: string, brand_closing: string, sms_confirmation: string}",
        "Customer: {customer_name} | Order: {order_name} | Total: ${order_total} | Items: {item_count} | Primary: {primary_item} | City: {shipping_city}",
        [
          { field: "email_subject", as: "email_subject", type: "string" },
          { field: "greeting", as: "greeting", type: "string" },
          { field: "order_highlight", as: "order_highlight", type: "string" },
          { field: "delivery_note", as: "delivery_note", type: "string" },
          { field: "brand_closing", as: "brand_closing", type: "string" },
          { field: "sms_confirmation", as: "sms_confirmation", type: "string" },
        ],
      ),

      // 4. Confirmation email
      emailNode(
        "4",
        "Send order confirmation email",
        760,
        "{customer_email}",
        "{email_subject}",
        "{greeting}\\n\\nOrder {order_name} confirmed!\\n\\n{order_highlight}\\n\\nOrder total: ${order_total}\\n\\n{delivery_note}\\n\\n{brand_closing}",
      ),

      // 5. SMS confirmation (Twilio)
      smsNode(
        "5",
        "Send SMS confirmation",
        1000,
        "{customer_phone}",
        "{sms_confirmation}",
      ),

      // 6. CRM update (HubSpot)
      hubspotNode(
        "6",
        "Update CRM with order",
        1240,
        "PATCH",
        "crm/v3/objects/contacts/{hubspot_contact_id}",
        [
          {
            key: "properties",
            value:
              '{"last_order_id":"{order_name}","last_order_value":"{order_total}","last_order_date":"{order_date}","last_order_item":"{primary_item}"}',
          },
        ],
        [{ name: "id", customName: "hubspot_updated_id", type: "string" }],
      ),

      // 7. Delivery logging (Sheets)
      sheetsNode(
        "7",
        "Log order confirmation to Sheets",
        1480,
        "OrderConfirmations",
        '[["{order_name}","{customer_email}","{customer_name}","{order_total}","{item_count}","{primary_item}","{shipping_city}","{hubspot_updated_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 445. ORDER CONFIRMATION EMAIL (7 nodes)
  // ===================================================================

  "Order Confirmation Email": {
    summary:
      "Sends type-specific order confirmation emails with personalized instructions. An order creation triggers order type detection via runCode, routes on template selection condition, generates AI personalized instructions, sends type-specific confirmation email, schedules downstream lifecycle, and logs template performance to Sheets.",
    nodes: [
      trigger("Webhook: Order created", "webhook_app", {
        source: "Shopify / E-commerce Platform",
        event: "orders/create",
        howItStarts:
          "A new order is created, triggering type-specific confirmation email with personalized instructions based on order contents",
        setup:
          "Configure order webhook. Define order types (physical, digital, subscription, gift) and corresponding templates.",
        inputFields: [],
      }),

      // 2. Order type detection (runCode)
      n("2", "runCode", "Detect order type", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Determine order type from line items and properties
const payload = vars.webhook_payload ? JSON.parse(vars.webhook_payload) : {};
const items = payload.line_items || [];
const hasPhysical = items.some(i => i.requires_shipping === true);
const hasDigital = items.some(i => i.requires_shipping === false);
const isSubscription = items.some(i => (i.properties || []).some(p => p.name === "subscription_id"));
const isGift = items.some(i => (i.properties || []).some(p => p.name === "gift_message"));
let orderType = "physical";
if (isSubscription) orderType = "subscription";
else if (isGift) orderType = "gift";
else if (hasDigital && !hasPhysical) orderType = "digital";
else if (hasDigital && hasPhysical) orderType = "mixed";
const result = {
  order_type: orderType,
  order_name: payload.name || "",
  order_total: payload.total_price || "0.00",
  customer_email: payload.email || "",
  customer_name: (payload.shipping_address || payload.billing_address || {}).first_name || "",
  item_count: items.length,
  primary_item: items[0] ? items[0].title : "",
  requires_shipping: hasPhysical ? "yes" : "no",
  has_digital: hasDigital ? "yes" : "no",
  gift_message: isGift ? items.find(i => (i.properties || []).some(p => p.name === "gift_message")).properties.find(p => p.name === "gift_message").value : ""
};
return result;`,
          outputVariables: [
            { name: "order_type", type: "string" },
            { name: "order_name", type: "string" },
            { name: "order_total", type: "string" },
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "item_count", type: "number" },
            { name: "primary_item", type: "string" },
            { name: "requires_shipping", type: "string" },
            { name: "has_digital", type: "string" },
            { name: "gift_message", type: "string" },
          ],
        },
      }),

      // 3. Template selection condition
      n("3", "condition", "Physical order requiring shipping?", 520, {
        conditionData: [
          {
            variable: "requires_shipping",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. AI personalized instructions
      aiNode(
        "4",
        "AI personalize order instructions",
        760,
        "You are an order confirmation specialist. Given the order type, generate type-specific instructions: for physical orders include shipping timeline and tracking info; for digital orders include download/access instructions; for subscriptions include billing cycle and management links; for gifts include gift wrapping confirmation and delivery message. Respond JSON: {subject_line: string, type_specific_header: string, instructions: string, next_steps: string, estimated_timeline: string, support_note: string}",
        "Order: {order_name} | Type: {order_type} | Total: ${order_total} | Customer: {customer_name} | Item: {primary_item} | Items: {item_count} | Shipping required: {requires_shipping} | Digital: {has_digital} | Gift message: {gift_message}",
        [
          { field: "subject_line", as: "subject_line", type: "string" },
          {
            field: "type_specific_header",
            as: "type_specific_header",
            type: "string",
          },
          { field: "instructions", as: "instructions", type: "string" },
          { field: "next_steps", as: "next_steps", type: "string" },
          {
            field: "estimated_timeline",
            as: "estimated_timeline",
            type: "string",
          },
          { field: "support_note", as: "support_note", type: "string" },
        ],
      ),

      // 5. Type-specific confirmation email
      emailNode(
        "5",
        "Send type-specific confirmation",
        1000,
        "{customer_email}",
        "{subject_line}",
        "Hi {customer_name},\\n\\n{type_specific_header}\\n\\nOrder: {order_name} | Total: ${order_total}\\nItem(s): {primary_item} (+{item_count} total)\\n\\n{instructions}\\n\\n{next_steps}\\n\\nTimeline: {estimated_timeline}\\n\\n{support_note}",
      ),

      // 6. Downstream lifecycle scheduling
      n("6", "hitApi", "Schedule lifecycle follow-ups", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{lifecycle_api}/api/v1/schedules",
          headers: [
            { key: "Authorization", value: "Bearer {lifecycle_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "customer_email", value: "{customer_email}" },
            { key: "order_type", value: "{order_type}" },
            { key: "order_id", value: "{order_name}" },
            { key: "trigger_at", value: "{estimated_timeline}" },
          ],
          variables: [
            {
              name: "schedule_id",
              customName: "lifecycle_schedule_id",
              type: "string",
            },
          ],
        },
      }),

      // 7. Template performance logging (Sheets)
      sheetsNode(
        "7",
        "Log template performance to Sheets",
        1480,
        "OrderConfirmationTemplates",
        '[["{order_name}","{order_type}","{order_total}","{customer_email}","{item_count}","{requires_shipping}","{has_digital}","{subject_line}","{lifecycle_schedule_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // physical -> AI instructions (shipping focus)
      e("3", "4", "output_2"), // digital/other -> AI instructions (access focus)
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 446. ORDER CONFIRMATION PLUS CROSS-SELL TEASER (7 nodes)
  // ===================================================================

  "Order Confirmation plus Cross-Sell Teaser": {
    summary:
      "Combines order confirmation with intelligent cross-sell recommendations. An order trigger initiates purchase analysis via runCode, AI generates complementary product recommendations, enriches recommendations from Shopify, sends a combined confirmation and teaser email, tracks cross-sell clicks, and logs incremental revenue to Sheets.",
    nodes: [
      trigger("Webhook: New order placed", "webhook_app", {
        source: "Shopify",
        event: "orders/create",
        howItStarts:
          "A new order is placed, triggering a confirmation email enhanced with AI-powered cross-sell product teasers",
        setup:
          "Configure Shopify order webhook. The workflow analyzes purchase patterns and recommends complementary products in the confirmation email.",
        inputFields: [],
      }),

      // 2. Purchase analysis (runCode)
      n("2", "runCode", "Analyze purchase for cross-sell", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze order for cross-sell opportunities
const payload = vars.webhook_payload ? JSON.parse(vars.webhook_payload) : {};
const items = payload.line_items || [];
const categories = [...new Set(items.map(i => i.product_type || i.vendor || "general"))];
const totalValue = parseFloat(payload.total_price || "0");
const itemTitles = items.map(i => i.title).join(", ");
const avgItemPrice = items.length > 0 ? (totalValue / items.length).toFixed(2) : "0";
const crossSellBudget = Math.min(totalValue * 0.5, 100).toFixed(2);
const result = {
  order_name: payload.name || "",
  order_total: payload.total_price || "0.00",
  customer_email: payload.email || "",
  customer_name: (payload.shipping_address || {}).first_name || "",
  item_titles: itemTitles,
  item_count: items.length,
  categories: categories.join(", "),
  avg_item_price: avgItemPrice,
  cross_sell_budget: crossSellBudget,
  primary_item: items[0] ? items[0].title : "",
  primary_product_id: items[0] ? String(items[0].product_id) : ""
};
return result;`,
          outputVariables: [
            { name: "order_name", type: "string" },
            { name: "order_total", type: "string" },
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "item_titles", type: "string" },
            { name: "item_count", type: "number" },
            { name: "categories", type: "string" },
            { name: "avg_item_price", type: "string" },
            { name: "cross_sell_budget", type: "string" },
            { name: "primary_item", type: "string" },
            { name: "primary_product_id", type: "string" },
          ],
        },
      }),

      // 3. AI complementary recommendations
      aiNode(
        "3",
        "AI recommend complementary products",
        520,
        "You are a retail cross-sell specialist. Given the purchased items, categories, and price range, recommend 3 complementary products that pair well with the purchase. Focus on genuine value — accessories, care products, or natural extensions of the purchase. Include a brief reason for each recommendation. Keep teaser copy subtle and helpful, not pushy. Respond JSON: {rec_1_title: string, rec_1_reason: string, rec_2_title: string, rec_2_reason: string, rec_3_title: string, rec_3_reason: string, teaser_headline: string, teaser_cta: string}",
        "Purchased: {item_titles} | Categories: {categories} | Order total: ${order_total} | Avg item price: ${avg_item_price} | Cross-sell budget: ${cross_sell_budget} | Customer: {customer_name}",
        [
          { field: "rec_1_title", as: "rec_1_title", type: "string" },
          { field: "rec_1_reason", as: "rec_1_reason", type: "string" },
          { field: "rec_2_title", as: "rec_2_title", type: "string" },
          { field: "rec_2_reason", as: "rec_2_reason", type: "string" },
          { field: "rec_3_title", as: "rec_3_title", type: "string" },
          { field: "rec_3_reason", as: "rec_3_reason", type: "string" },
          { field: "teaser_headline", as: "teaser_headline", type: "string" },
          { field: "teaser_cta", as: "teaser_cta", type: "string" },
        ],
      ),

      // 4. Enrichment (Shopify) — pull recommended product details
      shopifyNode(
        "4",
        "Enrich recommendations from Shopify",
        760,
        "GET",
        "products.json?title={rec_1_title}&limit=3",
        [],
        [
          {
            name: "products[0].variants[0].price",
            customName: "rec_1_price",
            type: "string",
          },
          {
            name: "products[0].images[0].src",
            customName: "rec_1_image",
            type: "string",
          },
          {
            name: "products[0].handle",
            customName: "rec_1_handle",
            type: "string",
          },
        ],
      ),

      // 5. Confirmation + teaser email
      emailNode(
        "5",
        "Send confirmation with cross-sell teaser",
        1000,
        "{customer_email}",
        "Order {order_name} Confirmed + Picks We Think You'll Love",
        "Hi {customer_name},\\n\\nGreat news — your order {order_name} is confirmed!\\n\\nOrder Summary:\\n{item_titles}\\nTotal: ${order_total}\\n\\nWe'll send tracking info once your order ships.\\n\\n---\\n\\n{teaser_headline}\\n\\n1. {rec_1_title} — {rec_1_reason}\\n2. {rec_2_title} — {rec_2_reason}\\n3. {rec_3_title} — {rec_3_reason}\\n\\n{teaser_cta}",
      ),

      // 6. Cross-sell click tracking
      n("6", "hitApi", "Track cross-sell engagement", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{analytics_api}/api/v1/events/cross-sell-tracking",
          headers: [
            { key: "Authorization", value: "Bearer {analytics_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "campaign", value: "order_confirmation_cross_sell" },
            { key: "order_id", value: "{order_name}" },
            {
              key: "recommendations",
              value: "{rec_1_title}, {rec_2_title}, {rec_3_title}",
            },
            { key: "order_total", value: "{order_total}" },
          ],
          variables: [
            {
              name: "tracked",
              customName: "tracking_confirmed",
              type: "boolean",
            },
          ],
        },
      }),

      // 7. Incremental revenue logging (Sheets)
      sheetsNode(
        "7",
        "Log cross-sell performance to Sheets",
        1480,
        "CrossSellTeaser",
        '[["{order_name}","{customer_email}","{order_total}","{item_count}","{categories}","{rec_1_title}","{rec_2_title}","{rec_3_title}","{cross_sell_budget}","{tracking_confirmed}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 447. ORDER DELIVERY CONFIRMATION AND FEEDBACK (9 nodes)
  // ===================================================================

  "Order Delivery Confirmation and Feedback": {
    summary:
      "Manages post-delivery experience from confirmation to feedback routing. A delivery webhook sends an 'everything okay?' confirmation email, waits 2 days, requests feedback, routes positive reviews vs negative issues to Zendesk, alerts CX on Slack for negatives, logs carrier attribution to Sheets, and suppresses review requests during issue resolution.",
    nodes: [
      trigger("Webhook: Order delivered", "webhook_app", {
        source: "Shopify / Shipping Carrier / AfterShip",
        event: "fulfillments/update (status: delivered)",
        howItStarts:
          "A delivery carrier confirms an order has been delivered, triggering the post-delivery confirmation and feedback collection flow",
        setup:
          "Configure delivery webhook from Shopify fulfillment updates or carrier integration (AfterShip, ShipStation). Include order ID, carrier, tracking number, and delivery timestamp.",
        inputFields: [],
      }),

      // 2. Confirmation email with "everything okay?"
      emailNode(
        "2",
        "Send delivery confirmation email",
        280,
        "{customer_email}",
        "Your order {order_name} has been delivered!",
        "Hi {customer_name},\\n\\nGreat news — your order {order_name} has been delivered!\\n\\nItem(s): {primary_item}\\nDelivered by: {carrier_name}\\nTracking: {tracking_number}\\n\\nIs everything okay with your order? If anything seems off, please let us know right away by replying to this email.\\n\\nEnjoy your purchase!",
      ),

      // 3. 2-day feedback delay
      n("3", "delayAction", "Wait 2 days for product experience", 520, {
        delayDetails: { delayType: "fixed", days: 2, hours: 0, minutes: 0 },
      }),

      // 4. Feedback request
      emailNode(
        "4",
        "Send feedback request email",
        760,
        "{customer_email}",
        "How's your {primary_item}? We'd love your feedback",
        "Hi {customer_name},\\n\\nYou've had your {primary_item} for a couple of days now. How is it?\\n\\nWe'd love to hear your thoughts:\\n\\nRate your experience (reply with a number 1-5):\\n5 - Love it!\\n4 - Really good\\n3 - It's okay\\n2 - Not great\\n1 - Disappointed\\n\\nYour feedback helps us improve and helps other customers make great choices.\\n\\nReply to this email or leave a review on our site.",
      ),

      // 5. Positive routing (condition)
      n("5", "condition", "Positive feedback received?", 1000, {
        conditionData: [
          {
            variable: "feedback_rating",
            value: "3",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Negative routing to Zendesk
      zendeskNode(
        "6",
        "Create support ticket for negative feedback",
        1240,
        "Negative Delivery Feedback: {order_name} - {customer_name}",
        "Customer {customer_name} ({customer_email}) reported a negative experience with order {order_name}.\\n\\nRating: {feedback_rating}/5\\nFeedback: {feedback_text}\\nItem: {primary_item}\\nCarrier: {carrier_name}\\nTracking: {tracking_number}\\n\\nPlease follow up within 24 hours to resolve the issue.",
        "high",
        [
          {
            name: "ticket.id",
            customName: "support_ticket_id",
            type: "string",
          },
          {
            name: "ticket.status",
            customName: "ticket_status",
            type: "string",
          },
        ],
        400,
      ),

      // 7. CX Slack alert
      slackNode(
        "7",
        "Slack: CX alert for negative feedback",
        1480,
        ":rotating_light: Negative Delivery Feedback\\nOrder: {order_name} | Rating: {feedback_rating}/5\\nCustomer: {customer_name} ({customer_email})\\nItem: {primary_item}\\nCarrier: {carrier_name}\\nFeedback: {feedback_text}\\nZendesk ticket: {support_ticket_id}",
      ),

      // 8. Carrier attribution logging (Sheets)
      sheetsNode(
        "8",
        "Log carrier attribution to Sheets",
        1720,
        "DeliveryFeedback",
        '[["{order_name}","{customer_email}","{primary_item}","{carrier_name}","{tracking_number}","{feedback_rating}","{feedback_text}","{support_ticket_id}"]]',
      ),

      // 9. Review suppression for issue resolution
      n("9", "runCode", "Suppress review request if issue open", 1960, {
        runCodeDetails: {
          language: "javascript",
          code: `// Determine whether to suppress automated review request
const rating = parseInt(vars.feedback_rating || "0", 10);
const hasOpenTicket = vars.support_ticket_id && vars.support_ticket_id !== "";
const result = {
  suppress_review: hasOpenTicket || rating <= 3 ? "yes" : "no",
  review_eligible: !hasOpenTicket && rating > 3 ? "yes" : "no",
  feedback_sentiment: rating >= 4 ? "positive" : rating === 3 ? "neutral" : "negative",
  resolution_required: hasOpenTicket ? "yes" : "no",
  carrier_performance: rating >= 4 ? "good" : rating === 3 ? "average" : "poor"
};
return result;`,
          outputVariables: [
            { name: "suppress_review", type: "string" },
            { name: "review_eligible", type: "string" },
            { name: "feedback_sentiment", type: "string" },
            { name: "resolution_required", type: "string" },
            { name: "carrier_performance", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "8", "output_1"), // positive -> log directly (skip ticket)
      e("5", "6", "output_2"), // negative -> create Zendesk ticket
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },
};
